import { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { compose } from 'redux';
import { withTranslation } from 'react-i18next';
import Text, { TEXT_SIZE, TEXT_COLOR } from '@components/Text';
import EmojiPickerButton from '@components/EmojiPickerButton';
import VoiceMessageComposer from '../VoiceMessageComposer';
import { createVoiceAttachment, createVoiceMessagePayload } from '../../services';
import {
  ClosedArea,
  InputArea,
  EmojiPickerWrapper,
  StyledTextArea,
  StyledCTAButton,
  VoiceErrorText,
  ActionsGroup,
} from './styles';

export class MessageArea extends Component {
  constructor(props) {
    super(props);
    this.messageTextArea = createRef();
    this.state = {
      value: '',
      hasVoiceDraft: false,
    };
  }

  componentDidMount() {
    this.messageTextArea.current?.focus();
  }

  componentDidUpdate(prevProps) {
    const { conversationPartner } = this.props;
    if (conversationPartner?.id !== prevProps.conversationPartner?.id) {
      this.messageTextArea.current?.focus();
    }
  }

  handleChange = (ev) => {
    const { value } = ev.target;
    this.setState({ value });
  };

  handleSubmit = () => {
    const { sendMessage } = this.props;
    const { value } = this.state;
    if (value.trim().length) {
      sendMessage({
        kind: 'text',
        text: value.trim(),
        attachments: [],
      });
    }
    this.setState({ value: '' });
  };

  hasTypedText = () => this.state.value.trim().length > 0;

  handleSendVoice = async ({ voiceAttachment, recordingDurationSeconds }) => {
    const { sendMessage } = this.props;
    sendMessage(createVoiceMessagePayload({
      voiceAttachment,
      recordingDurationSeconds,
    }));
  };

  createVoiceFile = async ({ file, fileName, recordingDurationSeconds }) => createVoiceAttachment({
    file,
    fileName,
    recordingDurationSeconds,
  });

  handleVoiceDraftStateChange = (isVoiceModeActive) => {
    if (!this.state.hasVoiceDraft && isVoiceModeActive) {
      this.setState({ value: '' });
    }
    this.setState({ hasVoiceDraft: isVoiceModeActive });
  };

  onEmojiSelect = (emoji) => {
    const textAreaNode = this.messageTextArea.current;
    const { value } = this.state;
    const start = textAreaNode?.selectionStart ?? value.length;
    const end = textAreaNode?.selectionEnd ?? value.length;
    const nextValue = `${value.slice(0, start)}${emoji}${value.slice(end)}`;
    const nextCursorPosition = start + emoji.length;

    this.setState(
      { value: nextValue },
      () => {
        if (this.messageTextArea.current) {
          this.messageTextArea.current.focus();
          this.messageTextArea.current.setSelectionRange(nextCursorPosition, nextCursorPosition);
        }
      }
    );
  };

  render() {
    const {
      currentConversation,
      conversationPartner,
      t,
    } = this.props;
    const {
      value,
      hasVoiceDraft,
    } = this.state;
    const isConversationClosed = !_.isEmpty(currentConversation)
      && Boolean(currentConversation.Thread.closed);
    const isClosedBySelf = isConversationClosed
      && currentConversation.Users.find(
        ({ id }) => id === currentConversation.Thread.closed.by,
      )?.self;
    const isAreaDisabled = _.isEmpty(conversationPartner);
    const hasText = this.hasTypedText();
    const sendActionDisabled = isAreaDisabled || !hasText || hasVoiceDraft;

    return isConversationClosed ? (
      <ClosedArea>
        <Text size={TEXT_SIZE.T1} textColor={TEXT_COLOR.DISABLED}>
          {`${t(`Informative.Notifying.${isClosedBySelf ? 'endedBySelf' : 'endedBy'}`)}${isClosedBySelf ? '' : ` ${conversationPartner?.fullName}`}`}
        </Text>
      </ClosedArea>
    ) : (
      <VoiceMessageComposer
        t={t}
        isAreaDisabled={isAreaDisabled}
        hasText={hasText}
        createVoiceFile={this.createVoiceFile}
        sendVoiceMessage={this.handleSendVoice}
        onVoiceDraftStateChange={this.handleVoiceDraftStateChange}
      >
        {({
          isVoiceModeActive,
          renderVoiceInput,
          renderActions,
          voiceError,
        }) => (
          <InputArea disabled={isAreaDisabled}>
            {isVoiceModeActive ? (
              renderVoiceInput
            ) : (
              <>
                <StyledTextArea
                  rows={5}
                  value={value}
                  onChange={this.handleChange}
                  placeholder={t('Common.Placeholders.message')}
                  disabled={isAreaDisabled}
                  ref={this.messageTextArea}
                />
                <EmojiPickerWrapper>
                  <EmojiPickerButton
                    onSelect={this.onEmojiSelect}
                    recentStorageKey='novotalk_recent_emojis_messages'
                  />
                </EmojiPickerWrapper>
              </>
            )}
            <ActionsGroup>
              {renderActions}
              {!isVoiceModeActive && (
                <StyledCTAButton
                  action
                  onClick={this.handleSubmit}
                  disabled={sendActionDisabled}
                >
                  {t('Common.Statuses.send')}
                </StyledCTAButton>
              )}
            </ActionsGroup>
            {voiceError && <VoiceErrorText>{voiceError}</VoiceErrorText>}
          </InputArea>
        )}
      </VoiceMessageComposer>
    );
  }
}

MessageArea.propTypes = {
  currentConversation: PropTypes.object,
  sendMessage: PropTypes.func,
  conversationPartner: PropTypes.object,
  t: PropTypes.func,
};

export default compose(
  withTranslation(),
)(MessageArea);
